import type { SortDirection } from "./useSortableRows";
import "./SortableColumnHeader.css";

export interface SortableColumnHeaderProps {
  label: string;
  sortKey: string;
  activeSortKey: string;
  direction: SortDirection;
  onSort: (key: string) => void;
  /** Optional extra class on the <th> itself — e.g. right-alignment for numeric columns (Analytics tables). Never required for the master-data tables this component originated in. */
  className?: string;
}

/**
 * One sortable `<th>` for master-data tables (Products/Operations/Workers —
 * NORMAX_PERFORMANCE_FIXED_WIDTH_CODES_SORTABLE_MASTER_DATA_IMPLEMENTATION_REPORT.md
 * §13). A native `<button>` inside the header cell gives click AND keyboard
 * (Tab + Enter/Space) operability for free — no custom key handling needed.
 * `aria-sort` on the `<th>` itself (not the button) is the correct ARIA
 * table-sort pattern, read out by screen readers without extra markup.
 */
export function SortableColumnHeader({
  label,
  sortKey,
  activeSortKey,
  direction,
  onSort,
  className,
}: SortableColumnHeaderProps) {
  const isActive = sortKey === activeSortKey;
  const ariaSort = isActive ? (direction === "asc" ? "ascending" : "descending") : "none";
  const indicator = isActive ? (direction === "asc" ? " ↑" : " ↓") : "";

  return (
    <th aria-sort={ariaSort} className={className}>
      <button type="button" className="nx-sortable-header" onClick={() => onSort(sortKey)}>
        {label}
        {indicator}
      </button>
    </th>
  );
}
